import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { MRT_ColumnFiltersState } from 'material-react-table';
import {
  FilterValueShape,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import {
  query,
  where,
  orderBy,
  getDocs,
  getCountFromServer,
  limit,
  startAfter,
} from 'firebase/firestore';
import { DR_Holding_Proxy } from '@rabbit/data/portal';
import TableRegistrations from '../components/molecules/TableRegistrations/TableRegistrations';
import { getUserPermissions, asRange } from '../utils/helpers';
import { UserContext } from '../context/UserContext';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import {
  DTBusiness_User_Invite,
  DTHoldingProxy,
  DTRetailer_Public,
  Permissions,
  FBD_MainGroup,
  AssociatedPartner,
} from '@rabbit/data/types';
import { AppContext } from '@rabbit/app-context';
import {
  FacetFilter,
  NumericFilter,
  SearchHoldingProxy,
} from '@rabbit/search/cherchons';

export type TenantType = 'carTenants' | 'tableTenants' | 'defaultTenants';

export enum TenantTypeEnum {
  DEFAUL_TENANT = 'defaultTenants',
  CAR_TENANT = 'carTenants',
  TABLE_TENANT = 'tableTenants',
}

export default function RegistrationsViewV2() {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const tenantLink = t('tenantLink');
  const userPermissions = getUserPermissions(tenantLink, userContext);
  const isPremiumTenant = userContext?.accessToken?.['W:' + tenantLink] === 'S';
  const {
    repairerPersonaId,
    retailerPersonaId,
    installerPersonaId,
    consumerPersonaId,
    activeTenantInfo,
  } = userContext ?? ({} as any);

  const { config, algoliaConfig, algoliaConfigLoading } =
    useContext(AppContext);

  const activePersonaIds = {
    repairer: repairerPersonaId,
    retailer: retailerPersonaId,
    installer: installerPersonaId,
    consumer: consumerPersonaId,
  };

  const isAdmin = userContext?.accessToken?.admin;
  const personasLoaded = !!userContext?.personasLoaded;
  const isSupagardTenant = tenantLink === 'SUPAGARD';

  //Set the filters default date range to this month -VP
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const beginDate = new Date(currentYear, currentMonth, 1);
  const endDate = today;
  const [mainGroupRetailers, setMainGroupRetailers] = useState<string[] | null>(
    null
  );

  const [partnerLoaded, setPartnerLoaded] = useState<boolean>(false);

  const [filters, setFilters] = useState<FilterValueShape>(() => {
    if (t('tenantLink') === 'WARRANTYIRELAND') {
      return {
        dateType: 'This month',
        beginDate: beginDate,
        endDate: endDate,
      };
    }

    return {};
  });
  let tenantType: TenantType = 'defaultTenants';

  // Compute effective date range for Firestore export via useGetHoldingList
  const { beginDate: effBeginDate, endDate: effEndDate } = useMemo(() => {
    if (filters.dateType === 'This month') {
      return {
        beginDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate(),
      };
    }
    if (filters.dateType === 'Last month') {
      return {
        beginDate: moment().subtract(1, 'months').startOf('month').toDate(),
        endDate: moment().subtract(1, 'months').endOf('month').toDate(),
      };
    }
    if (
      filters.dateType === 'Date Range' &&
      filters.beginDate &&
      filters.endDate
    ) {
      return {
        beginDate: filters.beginDate,
        endDate: filters.endDate,
      };
    }
    return { beginDate: undefined as any, endDate: undefined as any };
  }, [filters]);

  // Determine partner filter application for Firestore export
  const mustApplyPartnerFilter = useMemo(() => {
    const mainGroupId = activeTenantInfo?.info?.mainGroup;
    const allowPartnerFacet =
      !!config?.HOLDINGS?.REGISTRATION
        ?.SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION;
    return !!mainGroupId && allowPartnerFacet && !isAdmin;
  }, [
    activeTenantInfo,
    config?.HOLDINGS?.REGISTRATION
      ?.SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION,
    isAdmin,
  ]);

  // `tableHoldingList` from Algolia for feature server side pagination
  const [searchValue, setSearchValue] = useState<string>('');
  const [tableHoldingList, setTableHoldingList] = useState<{
    data: DTHoldingProxy[];
  }>({
    data: [],
  });
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    pageSize: 10, // Items per page
    totalRecords: 0,
  });
  const [sorting, setSorting] = useState<any[]>([
    { id: 'timeStamp', desc: true },
  ]);

  // Column filters from MaterialReactTable (advanced table)
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );

  useEffect(() => {
    const fetchAlgoliaData = async () => {
      if (!t('tenantLink') || t('tenantLink') === 'tenantLink') {
        return;
      }

      if (algoliaConfigLoading) {
        return;
      }

      // Wait for userContext to be available to determine admin status
      // Note: isAdmin can be undefined for non-admin users, which is valid
      if (
        !userContext ||
        !userContext.accessToken ||
        !personasLoaded ||
        !partnerLoaded
      ) {
        return;
      }

      // If tenant is part of a main group and user is not admin,
      // wait until we've resolved retailer partners before searching
      const mainGroupId = activeTenantInfo?.info?.mainGroup;
      const allowPartnerFacet =
        !!config?.HOLDINGS?.REGISTRATION
          ?.SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION;
      const mustApplyPartnerFilter =
        !!mainGroupId && allowPartnerFacet && !isAdmin;

      // Wait for retailer data to be resolved
      // if (mustApplyPartnerFilter && mainGroupRetailers === null) return;
      if (mustApplyPartnerFilter && mainGroupRetailers === null) {
        return;
      }

      setIsTableLoading(true);

      try {
        const numericFilters: NumericFilter[] = [];
        const mfrValue = `M:${t('tenantLink')}`;
        const facetFilters: FacetFilter[] = [{ name: 'mfr', value: mfrValue }];
        //  use mapping from algoliaConfig.sorting for sort columns (asc/desc)
        const sort =
          sorting.length > 0
            ? [
                `${
                  (algoliaConfig as any)?.sorting?.[sorting[0].id] ||
                  sorting[0].id
                }:${sorting[0].desc ? 'desc' : 'asc'}`,
              ]
            : ['tcreate:desc'];

        // Add retailer filters from shared location partners (if applicable)
        // Admin users should see all entries
        if (
          !isAdmin &&
          mainGroupRetailers &&
          Array.isArray(mainGroupRetailers) &&
          mainGroupRetailers.length > 0
        ) {
          facetFilters.push({
            name: 'warrantyRetailerLinks',
            value: mainGroupRetailers,
          });
        }

        /* ------------------------------ Voiding filter -----------------------------*/
        if (filters.showVoided === 'Show only voided') {
          facetFilters.push({ name: 'warranty_status', value: 'voided' });
        } else if (filters.showVoided === 'Hide voided') {
          facetFilters.push({ name: 'warranty_status', value: 'active' });
        }

        /* ------------------------------ This month filter ------------------------------ */
        if (filters.dateType === 'This month') {
          const beginDate = moment().startOf('month').valueOf();
          const endDate = moment().endOf('month').valueOf();
          numericFilters.push(
            { name: 'tcreate', operator: '>=', value: beginDate },
            { name: 'tcreate', operator: '<=', value: endDate }
          );
        }

        /* ------------------------------ Last month filter ------------------------------ */
        if (filters.dateType === 'Last month') {
          const beginDate = moment()
            .subtract(1, 'months')
            .startOf('month')
            .valueOf();
          const endDate = moment()
            .subtract(1, 'months')
            .endOf('month')
            .valueOf();

          numericFilters.push(
            { name: 'tcreate', operator: '>=', value: beginDate },
            { name: 'tcreate', operator: '<=', value: endDate }
          );
        }

        /* ------------------------------ Date range filter ------------------------------ */
        if (
          filters.dateType === 'Date Range' &&
          filters.beginDate &&
          filters.endDate
        ) {
          const beginDate = moment(filters.beginDate).startOf('day').valueOf();
          const endDate = moment(filters.endDate).endOf('day').valueOf();
          numericFilters.push(
            { name: 'tcreate', operator: '>=', value: beginDate },
            { name: 'tcreate', operator: '<=', value: endDate }
          );
        }

        // Build field-specific search using Algolia restrictSearchableAttributes
        let restrictToAttribues: string[] | undefined;
        let fieldSpecificQuery: string | undefined;

        if (Array.isArray(columnFilters)) {
          // Map table column ids to Algolia searchable attribute names
          const attributeMap: Record<string, string> = {
            modelNum: 'unordered(payload.mpn)',
            vehicleModel: 'unordered(payload.additionalData.model)',
            consumerName: 'unordered(payload.consumer_name)',
            vehicleMake: 'unordered(payload.additionalData.make)',
            registrationNumber: 'unordered(payload.additionalData.vehicleNo)',
            salesReferenceNumber:
              'unordered(payload.additionalData.salesReferenceNo)',
            uniqueCode: 'unordered(payload.additionalData.uniqueCode)',
            leatherUniqueCode:
              'unordered(payload.additionalData.leatherUniqueCode)',
            serialNum: 'unordered(payload.full)',
            warrantyId: 'unordered(payload.docid)',
            salesPerson: 'unordered(payload.additionalData.salesperson)',
            product: 'unordered(payload.title)',
          };

          for (const cf of columnFilters) {
            const { id, value } = cf || {};
            if (!id || value == null) continue;

            if (id === 'registered' || id === 'purchaseDate') {
              const fieldName =
                id === 'registered' ? 'tcreate' : 'purchase_time';
              const { from, to } = asRange(value);

              if (from) {
                numericFilters.push({
                  name: fieldName,
                  operator: '>=',
                  value: moment(from)
                    .startOf('day')
                    .subtract(1, 'day')
                    .valueOf(),
                });
              }
              if (to) {
                numericFilters.push({
                  name: fieldName,
                  operator: '<=',
                  value: moment(to).endOf('day').add(1, 'd').valueOf(),
                });
              }
              continue;
            }

            // Use the first applicable text filter to build a field-specific query
            const attr = attributeMap[id as string];
            if (attr && String(value).trim() !== '' && !fieldSpecificQuery) {
              restrictToAttribues = [attr];
              fieldSpecificQuery = String(value).trim();
              // Do not break here if future filters need numeric/date processing; we only pick the first text filter
            }
          }
        }

        // Determine final query: prefer global searchValue if provided; otherwise use fieldSpecificQuery
        const localQuery =
          (searchValue || '').trim() || fieldSpecificQuery || '';

        const options: any = {
          page: pagination.currentPage,
          hitsPerPage: pagination.pageSize,
          facetFilters,
          numericFilters,
          sort,
        };

        if (
          !searchValue &&
          restrictToAttribues &&
          restrictToAttribues.length > 0
        ) {
          options.restrictToAttribues = restrictToAttribues;
        }

        const result = await SearchHoldingProxy(localQuery, options as any);

        setTableHoldingList({
          data: (result?.items as unknown as DTHoldingProxy[]) || [],
        });

        if ((result as any)?.numitems != null) {
          setPagination((prev) => ({
            ...prev,
            totalRecords: (result as any).numitems,
          }));
        }
      } catch (error) {
        console.error('Algolia holdings fetch failed:', error);
        setTableHoldingList({ data: [] });
      } finally {
        setIsTableLoading(false);
      }
    };
    void fetchAlgoliaData();
  }, [
    algoliaConfigLoading,
    algoliaConfig,
    t,
    searchValue,
    pagination.currentPage,
    pagination.pageSize,
    filters,
    userContext?.accessToken, // This will trigger when auth is resolved
    activePersonaIds.retailer,
    mainGroupRetailers,
    sorting,
    personasLoaded,
    partnerLoaded,
    columnFilters,
  ]);

  useEffect(() => {
    const getPartnerRetailersList = async () => {
      // Prevent duplicate processing
      if (mainGroupRetailers !== null || !personasLoaded) return;

      // Wait for activeTenantInfo to be available
      if (!activeTenantInfo) return;

      try {
        if (
          config?.HOLDINGS?.REGISTRATION
            ?.SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION
        ) {
          const mainGroupId = activeTenantInfo?.info?.mainGroup;
          if (mainGroupId) {
            const mainGroupDoc = await FBD_MainGroup.get(mainGroupId);

            if (mainGroupDoc && mainGroupDoc.sharedLocation) {
              const allRetailers =
                mainGroupDoc.associatedPartners?.map(
                  (partner: AssociatedPartner) =>
                    `S:${partner.tenantLink || partner.businessUserInviteLink}`
                ) || [];
              setMainGroupRetailers(allRetailers);
            } else {
              // Main group not found or sharedLocation is false, set current retailer personaID in mainGroupRetailers
              setMainGroupRetailers([retailerPersonaId]);
            }
          } else {
            // No mainGroupId, set empty array immediately
            setMainGroupRetailers([]);
          }
        } else {
          // Feature not enabled, set empty array immediately
          setMainGroupRetailers([]);
        }
      } catch (error) {
        console.error('Error fetching main group retailers:', error);
        // On error, set empty array to unblock Algolia
        setMainGroupRetailers([]);
      } finally {
        setPartnerLoaded(true);
      }
    };

    getPartnerRetailersList();
  }, [
    config?.HOLDINGS?.REGISTRATION
      ?.SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION,
    activeTenantInfo,
    personasLoaded,
  ]);
  if (
    t('tenantLink') === 'WARRANTYIRELAND' ||
    t('tenantLink') === 'PINNACLEWARRANTIES'
  )
    tenantType = 'carTenants';
  if (t('tenantLink') === 'NUCOVER') tenantType = 'tableTenants';

  const onChangeFilters = (value: FilterValueShape) => {
    setFilters(value);
  };

  if (
    !userPermissions?.includes(Permissions.RegistrationsEdit) &&
    !userPermissions?.includes(Permissions.RegistrationsView) &&
    !userPermissions?.includes(Permissions.Owner)
  )
    return <Navigate to={SAGE_ROUTE_NAME.MANAGE_ACCOUNT} />;

  if (
    !activePersonaIds.repairer &&
    !activePersonaIds.retailer &&
    !activePersonaIds.installer &&
    !activePersonaIds.consumer &&
    !personasLoaded
  )
    return <LoadingSpinner size="sm" />;

  if (!isSupagardTenant) {
    return <Navigate to={SAGE_ROUTE_NAME.REGISTRATIONS} />;
  }

  return (
    <div className="relative z-10 flex w-full items-center justify-between py-4">
      <TableRegistrations
        isLoading={isTableLoading || !personasLoaded || !partnerLoaded}
        tenantType={tenantType}
        holdingList={tableHoldingList}
        filters={filters}
        onFilter={onChangeFilters}
        isRegularTenant={!isPremiumTenant}
        manualPagination={true}
        rowCount={pagination.totalRecords}
        pageIndex={pagination.currentPage}
        pageSize={pagination.pageSize}
        onPageChange={(pageIndex, pageSize) =>
          setPagination({
            currentPage: pageIndex,
            pageSize,
            totalRecords: pagination.totalRecords,
          })
        }
        setGlobalFilter={setSearchValue}
        globalFilter={searchValue}
        manualSorting={true}
        sorting={sorting}
        onSortChange={setSorting}
        manualSearch
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        onRequestExportAllData={async (
          onProgress?: (loaded: number, total: number) => void
        ) => {
          try {
            // Ensure prerequisites similar to paginated fetch
            if (
              !t('tenantLink') ||
              t('tenantLink') === 'tenantLink' ||
              !userContext ||
              !userContext.accessToken ||
              !personasLoaded ||
              !partnerLoaded
            ) {
              // Export should only work when auth/persona/partner is ready
              return [];
            }

            // Build Firestore query lazily (no hook on initial load)
            const dateFrom = effBeginDate
              ? moment(effBeginDate).startOf('day').valueOf()
              : 0;
            const dateTo = effEndDate
              ? moment(effEndDate).endOf('day').valueOf()
              : 999999999999999;

            const constraints: any[] = [
              where('tcreate', '>=', dateFrom),
              where('tcreate', '<=', dateTo),
              where('deleted', '==', false),
              where('mfr', '==', `M:${t('tenantLink')}`),
              orderBy('tcreate'),
            ];

            // Voided filter mimicking useGetHoldingList
            if ((filters as any).showVoided !== undefined) {
              constraints.push(
                where(
                  'hasWarranty',
                  '==',
                  (filters as any).showVoided !== 'Show only voided'
                )
              );
            }

            // Partner retailers (if applicable)
            if (
              mustApplyPartnerFilter &&
              Array.isArray(mainGroupRetailers) &&
              mainGroupRetailers.length > 0
            ) {
              constraints.push(
                where(
                  'warrantyRetailerLinks',
                  'array-contains-any',
                  mainGroupRetailers
                )
              );
            }

            const baseQuery = query(
              DR_Holding_Proxy.collection(),
              ...constraints
            );

            // Get total count for progress
            let total = 0;
            try {
              const agg = await getCountFromServer(baseQuery as any);
              total = (agg.data() as any)?.count || 0;
            } catch (e) {
              // count is best-effort; continue without it if it fails
              total = 0;
            }

            const pageSize = 2000;
            let loaded = 0;
            let allDocs: any[] = [];
            let lastDoc: any = null;

            while (true) {
              const pageQuery = lastDoc
                ? query(
                    DR_Holding_Proxy.collection(),
                    ...constraints,
                    startAfter(lastDoc),
                    limit(pageSize)
                  )
                : query(
                    DR_Holding_Proxy.collection(),
                    ...constraints,
                    limit(pageSize)
                  );

              const snapshot = await getDocs(pageQuery as any);
              const docs = snapshot.docs;
              if (docs.length === 0) break;

              allDocs.push(...docs.map((d: any) => d.data()));
              loaded += docs.length;

              // Report progress if callback provided and total known
              if (typeof onProgress === 'function') {
                try {
                  onProgress(loaded, total);
                } catch {}
              }

              if (docs.length < pageSize) {
                break;
              }
              lastDoc = docs[docs.length - 1];
            }

            // If count failed, still send a final progress tick
            if (typeof onProgress === 'function' && total === 0) {
              try {
                onProgress(allDocs.length, allDocs.length);
              } catch {}
            }

            const data = allDocs as unknown as DTHoldingProxy[];

            // If a global search is applied, filter client-side to respect searchValue
            const normalizedSearch = (searchValue || '').trim().toLowerCase();
            const filtered = normalizedSearch
              ? data.filter((h) => {
                  try {
                    const hay = JSON.stringify(h).toLowerCase();
                    return hay.includes(normalizedSearch);
                  } catch {
                    return false;
                  }
                })
              : data;

            return filtered;
          } catch (err) {
            console.error('Export-all fetch failed:', err);
            return [];
          }
        }}
      />
    </div>
  );
}
